// TODO: Refactor this form to use react-form-hook & zod validations

import {
  useCreateMeeting,
  useGetActiveStudentsByMentor,
  useSearchStudentsForMeeting,
} from '@actions';
import PrimaryButtonV3 from '@components/V3/PrimaryButtonV3';
import AgoraDatePicker from '@components/V3/Utils/InputsV3/AgoraDatePicker';
import AgoraNewInput from '@components/V3/Utils/InputsV3/AgoraNewInput';
import AgoraTimePicker from '@components/V3/Utils/InputsV3/AgoraTimePicker';
import Modal from '@components/V4/Modal/Modal';
import Select from '@components/V4/Select/Select';
import { useAuthState } from '@contexts/AuthContext';
import { CreateMeeting } from '@shared/apis/meetings.apis';
import { CloseModalProps, Course, Session } from '@shared/common';
import { EDUCATION_STRATEGY_REASON_OPTIONS } from '@shared/constants';
import { CompensationType, EducationStrategyReason, SystemMeetingType } from '@shared/enums';
import AgoraSpinner from '@utils/AgoraSpinner';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import useDebounce from '../../hooks/useDebounce';
import useToast from '../../hooks/useToast';
import useTrackMountUnmountEvent from '../../hooks/useTrackMountUnmountEvent';
import { ANALYTICS_EVENT_NAMES } from '../../utils/constants';
import { COLORS } from '../../v3/global';

type MeetingTypeOption = SystemMeetingType | 'normal';

type UserArg = { _id: string; fullName: string };

type Props = CloseModalProps & {
  name?: string;
  users?: UserArg[];
  enrollmentId?: string;
  date?: Moment;
  isOnHomePage?: boolean;
  defaultEnrollment?: { _id: string; course: Partial<Course> };
  defaultStudent?: {
    fullName?: string;
    id: string;
  };
  defaultSession?: Session;
  postSuccess?: () => void;
};

const MeetingModal = ({
  visible,
  hide,
  date,
  isOnHomePage,
  defaultEnrollment,
  defaultStudent,
  defaultSession,
  postSuccess,
}: Props) => {
  const [isCreating, setIsCreating] = useState(false);

  const { trackEvent } = useAnalytics();

  const toast = useToast();
  const { mutate: createMeeting } = useCreateMeeting({
    onSuccess(data) {
      trackEvent(ANALYTICS_EVENT_NAMES.SCHEDULE_MEETING_SUCCESS);
      toast.success('Meeting scheduled successfully!');
      if (data?.overlapWarning) toast.warning(data.overlapWarning, 'Warning', 8000);
      setIsCreating(false);
      postSuccess?.();
      hide();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error?.message ??
        'Failed to create meeting. Please try again later.';

      trackEvent(ANALYTICS_EVENT_NAMES.SCHEDULE_MEETING_FAIL, errorMessage);
      toast.error(errorMessage);
      setIsCreating(false);
    },
  });
  const { userId: mentorId, compensationType, isEducationStrategist } = useAuthState();

  const isEligibleForSystemMeetings =
    compensationType === CompensationType.Salaried && !!isEducationStrategist;

  const [meetingType, setMeetingType] = useState<MeetingTypeOption>('normal');
  const isSystemMeeting = meetingType !== 'normal';
  const isEducationStrategy = meetingType === SystemMeetingType.EducationStrategy;

  const [educationStrategyReason, setEducationStrategyReason] = useState<EducationStrategyReason>();
  const [educationStrategyNotes, setEducationStrategyNotes] = useState('');

  const [studentSearch, setStudentSearch] = useState('');
  const debouncedStudentSearch = useDebounce(studentSearch, 300);
  const [selectedSystemStudent, setSelectedSystemStudent] = useState<
    { _id: string; fullName: string } | undefined
  >(undefined);

  const { data: searchedStudents, isLoading: isSearchingStudents } = useSearchStudentsForMeeting(
    { search: debouncedStudentSearch },
    {
      enabled:
        isEligibleForSystemMeetings && isSystemMeeting && debouncedStudentSearch.trim().length >= 2,
    }
  );

  useTrackMountUnmountEvent(
    ANALYTICS_EVENT_NAMES.SCHEDULE_MEETING_MODAL_OPEN,
    ANALYTICS_EVENT_NAMES.SCHEDULE_MEETING_MODAL_CLOSE
  );

  const defaultTime = moment().set('minute', 0).add(1, 'h').format('HH:mm');

  const isMidnight = defaultTime === '00:00';

  const [meetingDetails, setMeetingDetails] = useState<
    Record<
      | Exclude<
          keyof CreateMeeting['payload'],
          'meetingType' | 'educationStrategyReason' | 'educationStrategyNotes'
        >
      | 'time',
      string
    >
  >({
    name: 'Agora Meeting',
    enrollmentId: defaultEnrollment?._id ? defaultEnrollment._id : '',
    mentorId: mentorId ?? '',
    students: defaultStudent ? defaultStudent.id : '',
    scheduledDate: '',
    agenda: '',
    time: defaultTime,
  });

  const [formValidity, setFormValidity] = useState({
    name: false,
    enrollmentId: !!defaultEnrollment,
    mentorId: false,
    students: !!defaultStudent,
    startDate: false,
    time: !!defaultTime,
    date: !!(isOnHomePage ? date : moment()),
    educationStrategyReason: false,
  });

  const [touched, setTouched] = useState({
    name: false,
    enrollmentId: !!defaultEnrollment,
    mentorId: false,
    students: !!defaultStudent,
    startDate: false,
    time: defaultTime ? true : false,
    date: !!(isOnHomePage ? date : moment()),
    educationStrategyReason: false,
  });

  const [selectedDate, setSelectedDate] = useState(() => {
    const initialDate = isOnHomePage ? date : moment();

    if (isMidnight) {
      return initialDate?.add(1, 'days');
    }

    return initialDate;
  });

  const {
    data: myStudentsData,
    isLoading: isStudentsLoading,
    isFetching: isStudentsFetching,
  } = useGetActiveStudentsByMentor({ staleTime: 0, refetchOnMount: 'always' });

  //Remove duplicates
  const myStudents = myStudentsData?.filter((student, idx, arr) => {
    return idx === arr.findIndex((s) => s?._id === student?._id);
  });

  const systemStudentOptions = useMemo(() => {
    const opts = (searchedStudents ?? []).map((student) => ({
      label: student.email ? `${student.fullName} · ${student.email}` : student.fullName,
      value: student._id,
    }));
    if (selectedSystemStudent && !opts.some((o) => o.value === selectedSystemStudent._id)) {
      opts.unshift({ label: selectedSystemStudent.fullName, value: selectedSystemStudent._id });
    }
    return opts;
  }, [searchedStudents, selectedSystemStudent]);

  const meetingTypeOptions = [
    { label: 'Normal', value: 'normal' },
    { label: 'Onboarding', value: SystemMeetingType.Onboarding },
    { label: 'Education Strategy', value: SystemMeetingType.EducationStrategy },
  ];

  // Merge prop and hook state and return single variable for clear rendering
  // There should only be one enrollmentId saved
  const selectedEnrollment = useMemo(() => {
    const enrollmentPool =
      myStudentsData?.filter((s) => s._id == meetingDetails.students)?.map((s) => s.enrollment) ??
      [];
    if (defaultEnrollment) {
      enrollmentPool.push(defaultEnrollment);
    }

    return enrollmentPool.find((e) => e?._id === meetingDetails.enrollmentId);
  }, [defaultEnrollment, meetingDetails.enrollmentId]);

  // Autofill meeting name
  useEffect(() => {
    if (!selectedEnrollment) {
      return;
    }

    const newName = `${selectedEnrollment?.course.title}`;

    setMeetingDetails({
      ...meetingDetails,
      name: newName,
    });
  }, [selectedEnrollment]);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    setSelectedSystemStudent(undefined);
    setStudentSearch('');
    setEducationStrategyReason(undefined);
    setEducationStrategyNotes('');
    setTouched((prev) => ({
      ...prev,
      educationStrategyReason: false,
    }));
    setMeetingDetails((prev) => ({
      ...prev,
      name: isSystemMeeting
        ? meetingType === SystemMeetingType.Onboarding
          ? 'Onboarding'
          : 'Education Strategy'
        : 'Agora Meeting',
      students: '',
      enrollmentId: '',
    }));
    setFormValidity((prev) => ({
      ...prev,
      students: false,
      enrollmentId: isSystemMeeting ? true : false,
      educationStrategyReason: false,
    }));
  }, [meetingType]);

  useEffect(() => {
    const selectedTime = moment(meetingDetails.time, 'H:mm');
    const startDate = moment(
      selectedDate?.set({
        hour: selectedTime.get('hour'),
        minute: selectedTime.get('minute'),
        second: 0,
        millisecond: 0,
      })
    ).toISOString();

    if (moment().isAfter(startDate)) {
      toast.error('You cannot schedule a meeting in the past.');
    } else {
      setMeetingDetails({
        ...meetingDetails,
        scheduledDate: startDate,
      });
    }
  }, [meetingDetails.time, selectedDate]);

  const submit = () => {
    trackEvent(ANALYTICS_EVENT_NAMES.SCHEDULE_MEETING_TRIGGER);

    const isFormValid = runFormValidation();

    if (isFormValid) {
      setIsCreating(true);
      try {
        const body: CreateMeeting['payload'] = {
          name: meetingDetails.name,
          mentorId: meetingDetails.mentorId,
          students: [meetingDetails.students],
          scheduledDate: meetingDetails.scheduledDate,
          agenda: meetingDetails.agenda,
          ...(isSystemMeeting
            ? { meetingType: meetingType as SystemMeetingType }
            : { enrollmentId: meetingDetails.enrollmentId }),
          ...(isEducationStrategy
            ? {
                educationStrategyReason,
                educationStrategyNotes: educationStrategyNotes.trim() || undefined,
              }
            : {}),
        };

        createMeeting(body);
      } catch (error: any) {
        toast.error(error?.message ?? 'Failed to create meeting. Please try again later.');
        setIsCreating(false);
      }
    }
  };

  const runFormValidation = () => {
    setTouched({
      name: true,
      enrollmentId: true,
      mentorId: true,
      students: true,
      startDate: true,
      time: true,
      date: true,
      educationStrategyReason: true,
    });

    const isValidLength = (field: string) => (field && field.length > 1) as boolean;

    const newFormValidity = {
      name: isValidLength(meetingDetails.name),
      enrollmentId: isSystemMeeting ? true : isValidLength(meetingDetails.enrollmentId),
      mentorId: isValidLength(meetingDetails.mentorId),
      students: isValidLength(meetingDetails.students),
      startDate: isValidLength(meetingDetails.scheduledDate),
      time: isValidLength(meetingDetails.time),
      date: isValidLength(meetingDetails.scheduledDate),
      educationStrategyReason: isEducationStrategy ? !!educationStrategyReason : true,
    };

    setFormValidity(newFormValidity);

    return Object.values(newFormValidity).every(Boolean);
  };

  const runIndividualFormValidation = (field: string, input?: string) => {
    let isValid = true;

    // GENERAL
    if (input === undefined || input.length < 1) {
      isValid = false;
    }

    setFormValidity({
      ...formValidity,
      [field]: isValid,
    });

    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handleBlur = (
    event: React.FocusEvent<HTMLInputElement>,
    fieldName?: string,
    inputValue?: string
  ) => {
    event.preventDefault();
    const { name, value } = event.target;

    const field = fieldName === undefined ? name : fieldName;
    const input = value ? value : inputValue;

    runIndividualFormValidation(field, input);
  };

  return (
    <Modal isOpen={visible} onClose={hide}>
      <Modal.Header title="Schedule Meeting" className="mb-6" />
      <Modal.Body>
        {isStudentsLoading || isStudentsFetching ? (
          <div className="my-10">
            <AgoraSpinner fontSize={48} />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              {/* MEETING TYPE */}
              {isEligibleForSystemMeetings && (
                <Select
                  size="large"
                  label="Meeting type"
                  showSearch={false}
                  allowClear={false}
                  value={meetingType}
                  options={meetingTypeOptions}
                  onSelect={(value) => setMeetingType((value as MeetingTypeOption) ?? 'normal')}
                />
              )}

              <div className="flex flex-col tablet:flex-row justify-between gap-4 tablet:gap-6">
                {/* STUDENT */}
                <Select
                  size="large"
                  label="Student"
                  filterOption={isSystemMeeting ? false : undefined}
                  isLoading={isSystemMeeting ? isSearchingStudents : false}
                  onSearch={isSystemMeeting ? (value) => setStudentSearch(value) : undefined}
                  notFoundContent={
                    isSystemMeeting && debouncedStudentSearch.trim().length < 2
                      ? 'Type at least 2 characters to search'
                      : undefined
                  }
                  onSelect={(value) => {
                    if (value === undefined || value === meetingDetails.students) {
                      return;
                    }

                    if (isSystemMeeting) {
                      const picked = (searchedStudents ?? []).find((s) => s._id === value);
                      if (picked) {
                        setSelectedSystemStudent(picked);
                      }
                      setMeetingDetails({
                        ...meetingDetails,
                        students: value as string,
                      });
                      setFormValidity({
                        ...formValidity,
                        students: true,
                      });
                      return;
                    }

                    setFormValidity({
                      ...formValidity,
                      enrollmentId: false,
                    });
                    setMeetingDetails({
                      ...meetingDetails,
                      students: value as string,
                      // also reset the enrollmentId in case a previous student & enrollment has been selected
                      // in order to make sure that we do not send an enrolmentId that's invalid for the given student
                      enrollmentId: '',
                    });
                  }}
                  value={meetingDetails.students.length ? meetingDetails.students : undefined}
                  options={
                    isSystemMeeting
                      ? systemStudentOptions
                      : myStudents?.map((student) => ({
                          label: student.fullName,
                          value: student._id,
                        }))
                  }
                  onClear={() => {
                    if (isSystemMeeting) {
                      setSelectedSystemStudent(undefined);
                      setStudentSearch('');
                      setMeetingDetails({
                        ...meetingDetails,
                        students: '',
                      });
                      setFormValidity({
                        ...formValidity,
                        students: false,
                      });
                      return;
                    }

                    setMeetingDetails({
                      ...meetingDetails,
                      students: '',
                      // also reset the enrollmentId in case a previous student & enrollment has been selected
                      // in order to make sure that we do not send an enrolmentId that's invalid for the given student
                      enrollmentId: '',
                    });
                    setFormValidity({
                      ...formValidity,
                      students: false,
                      enrollmentId: false,
                    });
                  }}
                  onBlur={(e) => handleBlur(e, 'students', meetingDetails.students)}
                  isRequired
                  isTouched={touched.students}
                  isValid={formValidity.students}
                  errorText="You need to select a student."
                />

                {/* COURSE */}
                {!isSystemMeeting && (
                  <Select
                    size="large"
                    label="Course"
                    onSelect={(value) => {
                      if (value !== undefined)
                        setMeetingDetails({
                          ...meetingDetails,
                          enrollmentId: value as string,
                        });
                    }}
                    value={
                      meetingDetails.enrollmentId.length ? meetingDetails.enrollmentId : undefined
                    }
                    onClear={() => {
                      setMeetingDetails({
                        ...meetingDetails,
                        enrollmentId: '',
                      });
                      setFormValidity({
                        ...formValidity,
                        enrollmentId: false,
                      });
                    }}
                    options={myStudentsData
                      ?.filter((s) => s._id === meetingDetails.students && s.enrollment)
                      ?.map((s) => ({
                        label: s.enrollment.course.title ?? 'Missing course title',
                        value: s.enrollment._id,
                      }))}
                    onBlur={(e) => handleBlur(e, 'enrollmentId', meetingDetails.enrollmentId)}
                    isRequired
                    isTouched={touched.enrollmentId}
                    isValid={formValidity.enrollmentId}
                    errorText="You need to select a course."
                  />
                )}

                {/* EDUCATION STRATEGY REASON */}
                {isEducationStrategy && (
                  <Select
                    size="large"
                    label="Reason"
                    showSearch={false}
                    allowClear={false}
                    placeholder="Select a reason"
                    value={educationStrategyReason}
                    options={EDUCATION_STRATEGY_REASON_OPTIONS}
                    onSelect={(value) => {
                      if (value === null) {
                        return;
                      }

                      setEducationStrategyReason(value as EducationStrategyReason);
                      setFormValidity((prev) => ({
                        ...prev,
                        educationStrategyReason: true,
                      }));
                    }}
                    onBlur={(e) =>
                      handleBlur(e, 'educationStrategyReason', educationStrategyReason)
                    }
                    isRequired
                    isTouched={touched.educationStrategyReason}
                    isValid={formValidity.educationStrategyReason}
                    errorText="You need to select a reason."
                  />
                )}
              </div>

              {/* DATETIME */}
              <div className="flex flex-col tablet:flex-row justify-between gap-4 tablet:gap-6">
                {/* DATE */}
                <AgoraDatePicker
                  allowClear={false}
                  value={selectedDate}
                  onChange={(value) => setSelectedDate(moment(value))}
                  disabledDate={(date) => date < moment(moment().format('YYYY-MM-DD'))}
                  className={`agora-date-picker ${formValidity.date && touched.date && 'isValid'}`}
                  onBlur={(e) => handleBlur(e, 'date', selectedDate?.toString())}
                  isTouched={touched.date}
                  isValid={formValidity.date}
                  errorText="You need to select a date."
                  isRequired
                  label="Date"
                />

                {/* TIME */}
                <AgoraTimePicker
                  time={meetingDetails.time}
                  handleChange={(value: string) => {
                    setMeetingDetails({
                      ...meetingDetails,
                      time: value,
                    });
                  }}
                  handleFocus={() => null}
                  date={selectedDate}
                  disablePastHours={true}
                  onBlur={(e) => handleBlur(e, 'time', meetingDetails.time)}
                  isTouched={touched.time}
                  isValid={formValidity.time}
                  label="Time"
                  errorText="You need to select a time."
                  isRequired
                />
              </div>
            </div>

            {/* AGENDA */}
            <AgoraNewInput
              htmlFor="agenda"
              name="agenda"
              id="agenda"
              type="textarea"
              placeholder="Type in a meeting agenda"
              label="Agenda"
              value={meetingDetails.agenda}
              rows={3}
              onChange={(e) => {
                setMeetingDetails({
                  ...meetingDetails,
                  agenda: e.target.value,
                });
              }}
              onBlur={handleBlur}
            />

            {/* EDUCATION STRATEGY NOTES */}
            {isEducationStrategy && (
              <AgoraNewInput
                htmlFor="educationStrategyNotes"
                name="educationStrategyNotes"
                id="educationStrategyNotes"
                type="textarea"
                placeholder="Add any context for this meeting"
                label="Notes"
                value={educationStrategyNotes}
                rows={3}
                onChange={(e) => setEducationStrategyNotes(e.target.value)}
              />
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="mt-12">
        <PrimaryButtonV3
          style={{ height: 40, width: '100%' }}
          buttonText={
            <div className="flex">
              {isCreating && (
                <AgoraSpinner
                  fontSize={14}
                  color={isCreating ? COLORS.TEXT_PRIMARY : COLORS.BLACK}
                  margin="0 8px 0 0"
                />
              )}

              <span
                style={{
                  color: isCreating ? COLORS.TEXT_PRIMARY : COLORS.BLACK,
                }}
              >
                Schedule Meeting
              </span>
            </div>
          }
          onMouseDown={() => {
            if (!isCreating) {
              submit();
            }
          }}
          disabled={isCreating}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default MeetingModal;
